import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../../api/adminApi';
import { Avatar, PlanBadge, cardStyle, sectionTitleStyle, statLabelStyle, LoadingState, ErrorState, timeAgo, formatDate, formatCurrency } from './shared';

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<api.AdminUserDetail | null>(null);
  const [usageDetail, setUsageDetail] = useState<api.AdminUserUsageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const [detail, usage] = await Promise.all([
        api.getUserDetail(userId),
        api.getUserUsageDetail(userId).catch(() => null),
      ]);
      setData(detail);
      setUsageDetail(usage);
    } catch (err: unknown) { setError((err as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userId]);

  if (loading) return <LoadingState />;
  if (error || !data) return <ErrorState message={error ?? 'User not found'} onRetry={load} />;

  const { user, subscription, usage, integrations, recentConversations, recentPayments } = data;
  const effectivePlan = usageDetail?.effectivePlan;
  const runs = usageDetail?.recentRuns ?? [];
  const runSummary = usageDetail?.runSummary;

  const subAmount = subscription ? (
    subscription.plan === 'enterprise' ? '$199.00/mo' :
    subscription.billingCycle === 'annual' ? '$152.00/yr' : '$19.00/mo'
  ) : null;

  const statusColor: Record<string, string> = { active: '#22883F', past_due: '#D44A3A', trialing: '#D46A3A', canceled: '#8B7D6B' };

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ fontSize: 13, color: '#8B7D6B', marginBottom: 16 }}>
        <span style={{ color: '#D46A3A', cursor: 'pointer' }} onClick={() => navigate('/admin/users')}>Users</span>
        <span style={{ margin: '0 8px' }}>/</span>
        <span>{user.displayName}</span>
      </div>

      {/* User Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar name={user.displayName} size={52} />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1A1714', margin: 0 }}>{user.displayName}</h1>
              <PlanBadge plan={effectivePlan?.plan ?? user.plan} />
              {effectivePlan && effectivePlan.source !== 'subscription' && effectivePlan.plan !== 'free' && (
                <span style={{ fontSize: 10, color: '#D46A3A', fontWeight: 500 }}>({effectivePlan.source})</span>
              )}
              <span style={{ color: user.isActive ? '#22883F' : '#D44A3A', fontSize: 16 }}>●</span>
            </div>
            <div style={{ fontSize: 13, color: '#8B7D6B', marginTop: 4 }}>
              {user.email} · Joined {formatDate(user.createdAt)}{user.lastLoginAt ? ` · Last login ${timeAgo(user.lastLoginAt)}` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #E8E0D8', backgroundColor: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>Edit User</button>
          <button style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #D44A3A', backgroundColor: '#fff', color: '#D44A3A', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif' }}>Deactivate</button>
        </div>
      </div>

      {/* Info Cards Row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {/* Subscription */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <div style={statLabelStyle}>SUBSCRIPTION</div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <InfoRow label="Plan" value={subscription ? `${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} — ${subscription.billingCycle.charAt(0).toUpperCase() + subscription.billingCycle.slice(1)}` : 'Free'} />
            {subAmount && <InfoRow label="Amount" value={<span style={{ fontFamily: '"JetBrains Mono", monospace' }}>{subAmount}</span>} />}
            <InfoRow label="Status" value={<span style={{ color: statusColor[subscription?.status ?? 'free'] ?? '#8B7D6B', fontWeight: 500 }}>{subscription ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1).replace('_', ' ') : 'Free'}</span>} />
            {subscription?.currentPeriodEnd && <InfoRow label="Renews" value={formatDate(subscription.currentPeriodEnd)} />}
            {subscription?.paymentMethod?.last4 && <InfoRow label="Payment" value={`${subscription.paymentMethod.brand ?? 'Card'} ····${subscription.paymentMethod.last4}`} />}
          </div>
        </div>

        {/* Usage */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <div style={statLabelStyle}>{new Date(usage.month + '-01').toLocaleString('en-US', { month: 'long' }).toUpperCase()} USAGE</div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <InfoRow label="AI Conversations" value={String(usage.aiConversations)} />
            <InfoRow label="File Operations" value={String(usage.fileOperations)} />
            <InfoRow label="E-Sign Docs" value={String(usage.esignDocuments)} />
          </div>
        </div>

        {/* Token Usage (V2) */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <div style={statLabelStyle}>TOKEN USAGE</div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <InfoRow label="Model Tokens" value={formatTokens(usage.modelTokens)} />
            <InfoRow label="Model Cost" value={formatUsd(usage.modelCostUsd)} />
            <InfoRow label="Embedding Tokens" value={formatTokens(usage.embeddingTokens)} />
            <InfoRow label="Embedding Cost" value={formatUsd(usage.embeddingCostUsd)} />
            <InfoRow label="Retries" value={`${usage.retryCount} (${formatTokens(usage.retryTokens)} tokens)`} />
            <InfoRow label="Retry Cost" value={formatUsd(usage.retryCostUsd)} />
          </div>
        </div>
      </div>

      {/* Second Row: Integrations + Run Summary */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {/* Integrations */}
        <div style={{ ...cardStyle, flex: 1 }}>
          <div style={statLabelStyle}>INTEGRATIONS</div>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {integrations.length > 0 ? integrations.map((int) => (
              <div key={int.provider} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, color: '#1A1714', textTransform: 'capitalize' }}>{int.provider.replace('_', ' ')}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: int.status === 'active' ? '#22883F' : int.status === 'error' ? '#D44A3A' : '#8B7D6B' }}>
                  {int.status === 'active' ? '● Connected' : int.status === 'error' ? '● Error' : 'Not connected'}
                </span>
              </div>
            )) : (
              <span style={{ fontSize: 13, color: '#8B7D6B' }}>No integrations connected</span>
            )}
          </div>
        </div>

        {/* Query Run Summary */}
        {runSummary && (
          <div style={{ ...cardStyle, flex: 1 }}>
            <div style={statLabelStyle}>QUERY RUNS (RECENT 20)</div>
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <InfoRow label="Total Runs" value={String(runSummary.totalRuns)} />
              <InfoRow label="Completed" value={<span style={{ color: '#22883F', fontWeight: 500 }}>{runSummary.completedRuns}</span>} />
              <InfoRow label="Failed" value={<span style={{ color: runSummary.failedRuns > 0 ? '#D44A3A' : '#8B7D6B', fontWeight: 500 }}>{runSummary.failedRuns}</span>} />
              <InfoRow label="Partial" value={<span style={{ color: runSummary.partialRuns > 0 ? '#D46A3A' : '#8B7D6B', fontWeight: 500 }}>{runSummary.partialRuns}</span>} />
              <InfoRow label="Cancelled" value={String(runSummary.cancelledRuns)} />
            </div>
          </div>
        )}
      </div>

      {/* Bottom Section: Recent Conversations + Payment History + Recent Runs */}
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Recent Conversations</span>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
            {recentConversations.map((c, i) => (
              <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: i < recentConversations.length - 1 ? '1px solid #F0EBE4' : 'none' }}>
                <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: '#F5F2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B7D6B" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: '#8B7D6B', marginTop: 2 }}>{c.toolsUsed?.join(' · ') || '—'}</div>
                </div>
                <span style={{ fontSize: 13, color: '#8B7D6B', whiteSpace: 'nowrap' }}>{timeAgo(c.lastMessageAt)}</span>
              </div>
            ))}
            {recentConversations.length === 0 && <span style={{ fontSize: 13, color: '#8B7D6B' }}>No conversations</span>}
          </div>
        </div>

        <div style={{ ...cardStyle, flex: 1 }}>
          <span style={sectionTitleStyle}>Payment History</span>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column' }}>
            {recentPayments.map((p, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < recentPayments.length - 1 ? '1px solid #F0EBE4' : 'none' }}>
                <span style={{ fontSize: 13, color: '#8B7D6B' }}>{p.paidAt ? formatDate(p.paidAt) : '—'}</span>
                <span style={{ fontSize: 14, fontWeight: 500, fontFamily: '"JetBrains Mono", monospace' }}>{formatCurrency(p.amount)}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: p.status === 'paid' ? '#22883F' : p.status === 'failed' ? '#D44A3A' : '#D46A3A' }}>
                  {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                </span>
                <span style={{ fontSize: 13, color: '#D46A3A', cursor: 'pointer', fontWeight: 500 }}>Invoice</span>
              </div>
            ))}
            {recentPayments.length === 0 && <span style={{ fontSize: 13, color: '#8B7D6B' }}>No payments</span>}
          </div>
        </div>
      </div>

      {/* Recent Query Runs */}
      {runs.length > 0 && (
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <span style={sectionTitleStyle}>Recent Query Runs</span>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
            <thead>
              <tr>
                <th style={{ ...thStyle }}>Query</th>
                <th style={{ ...thStyle }}>Status</th>
                <th style={{ ...thStyle }}>Tasks</th>
                <th style={{ ...thStyle }}>Tokens</th>
                <th style={{ ...thStyle }}>Cost</th>
                <th style={{ ...thStyle }}>Retries</th>
                <th style={{ ...thStyle }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.runId}>
                  <td style={tdStyle}>{run.query}</td>
                  <td style={tdStyle}>
                    <span style={{ color: run.status === 'completed' ? '#22883F' : run.status === 'failed' ? '#D44A3A' : run.status === 'partial' ? '#D46A3A' : '#8B7D6B', fontWeight: 500, fontSize: 13 }}>
                      {run.status}
                    </span>
                  </td>
                  <td style={tdStyle}>{run.taskCount}</td>
                  <td style={{ ...tdStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}>{run.totalTokensUsed != null ? formatTokens(run.totalTokensUsed) : '—'}</td>
                  <td style={{ ...tdStyle, fontFamily: '"JetBrains Mono", monospace', fontSize: 12 }}>{run.estimatedCostUsd != null ? formatUsd(run.estimatedCostUsd) : '—'}</td>
                  <td style={tdStyle}>{run.userRetryCount || '—'}</td>
                  <td style={{ ...tdStyle, color: '#8B7D6B' }}>{run.startedAt ? timeAgo(run.startedAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 14, color: '#8B7D6B' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>{value}</span>
    </div>
  );
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatUsd(n: number): string {
  return `$${n.toFixed(4)}`;
}

const thStyle: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 600, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #F0EBE4' };
const tdStyle: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#1A1714', borderBottom: '1px solid #F0EBE4' };
